import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import '../Reports.scss';
import Button from '../../Common/Button';
import InputText from '../../Common/InputText';
import { CsvToIntArray } from '../../../helpers/strings';
import { getMilestoneIds, getDateValue } from '../../../helpers/reports';

const COLUMNS_TO_EXCLUDE = [
    { id: 'Area', name: 'Area' },
    { id: 'Brand', name: 'Brand' },
    { id: 'CoachEID', name: 'Coach EID' },
    { id: 'CoachEmailAddress', name: 'Coach Email Address' },
    { id: 'CoachName', name: 'Coach Name' },
    { id: 'LocationType', name: 'Location Type' },
    { id: 'ManagementCompany', name: 'Management Company' },
    { id: 'Region', name: 'Region' },
    { id: 'ZeroDate', name: 'Zero Date' }
];

export class ProjectCrosstabFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            areaIds: '',
            baselineIds: '',
            brandIds: '',
            continentIds: '',
            countryIds: '',
            departmentIds: '',
            disciplineIds: '',
            excludedColumnIds: '',
            generation: '', // text-field
            locationTypeIds: '',
            managementCompanyIds: '',
            milestoneIds: '',
            projectId: '', // single-select
            projectZeroDate: '', // single-select
            regionIds: '',
        }

        this.state = {
            emptyForm: initialFormSelections,
            showMoreFields: false,

            excludedColumnOptions: COLUMNS_TO_EXCLUDE,

            areaIds: initialFormSelections.areaIds,
            baselineIds: initialFormSelections.baselineIds,
            brandIds: initialFormSelections.brandIds,
            continentIds: initialFormSelections.continentIds,
            countryIds: initialFormSelections.countryIds,
            departmentIds: initialFormSelections.departmentIds,
            disciplineIds: initialFormSelections.disciplineIds,
            excludedColumnIds: initialFormSelections.excludedColumnIds,
            generation: initialFormSelections.generation,
            locationTypeIds: initialFormSelections.locationTypeIds,
            managementCompanyIds: initialFormSelections.managementCompanyIds,
            milestoneIds: initialFormSelections.milestoneIds,
            projectId: initialFormSelections.projectId,
            projectZeroDate: initialFormSelections.projectZeroDate,
            regionIds: initialFormSelections.regionIds,
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = { ...this.state };

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,

                areaIds: appState.areaIds ? appState.areaIds : newState.areaIds,
                brandIds: appState.brandIds ? appState.brandIds : newState.brandIds,
                baselineIds: appState.baselineIds ? appState.baselineIds : newState.baselineIds,
                continentIds: appState.continentIds ? appState.continentIds : newState.continentIds,
                countryIds: appState.countryIds ? appState.countryIds : newState.countryIds,
                departmentIds: appState.departmentIds ? appState.departmentIds : newState.departmentIds,
                disciplineIds: appState.disciplineIds ? appState.disciplineIds : newState.disciplineIds,
                excludedColumnIds: appState.excludedColumnIds ? appState.excludedColumnIds : newState.excludedColumnIds,
                generation: appState.generation ? appState.generation : newState.generation,
                locationTypeIds: appState.locationTypeIds ? appState.locationTypeIds : newState.locationTypeIds,
                managementCompanyIds: appState.managementCompanyIds ? appState.managementCompanyIds : newState.managementCompanyIds,
                milestoneIds: appState.milestoneIds ? appState.milestoneIds : newState.milestoneIds,
                projectId: appState.projectId ? appState.projectId : newState.projectId,
                projectZeroDate: appState.projectZeroDate ? appState.projectZeroDate : newState.projectZeroDate,
                regionIds: appState.regionIds ? appState.regionIds : newState.regionIds,
            }
        }

        let projectId = this.getDefaultProjectId(newState.projectId);

        // Possibly override app state values with default values or URL params
        newState = {
            ...newState,
            projectId: projectId
        }

        this.setState({
            ...this.state,
            ...newState
        });

        /**
         * If newState.projectId has a value, then we need to trigger a project
         * change to make sure we immediately load the ProjectZeroDate and 
         * Project Baseline options, because this does not get automatically 
         * triggered just by setting the value in state.
         */
        if (newState.projectId) {
            this.props.onProjectChange(newState.projectId);
        }
    }

    isProjectEnabled = () => {
        this.props.filters && this.props.filters.isProjectEnabled === true;
    }

    getDefaultProjectId = (savedProjectId) => {
        /**
         * Project IDs(s) can be sent in via the URL params.  If they are set there
         * then we override the default or saved values and select the values from
         * the URL params
         */
        let projectId = '';
        const urlProjectId = this.props.urlParams.projectId;
        if (urlProjectId && urlProjectId.trim().length > 0) {
            projectId = urlProjectId;
        } else if (savedProjectId) {
            projectId = savedProjectId;
        }

        return projectId;
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        if (!this.state.projectId) {
            return false;
        }

        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.areaIds = (reportRequest.areaIds) ? reportRequest.areaIds.join(',') : form.areaIds;
        form.baselineIds = (reportRequest.baselineIds) ? reportRequest.baselineIds.join(',') : form.baselineIds;
        form.brandIds = (reportRequest.brandIds) ? reportRequest.brandIds.join(',') : form.brandIds;
        form.continentIds = (reportRequest.continentIds) ? reportRequest.continentIds.join(',') : form.continentIds;
        form.countryIds = (reportRequest.countryIds) ? reportRequest.countryIds.join(',') : form.countryIds;
        form.excludedColumnIds = (reportRequest.columnKeys) ? reportRequest.columnKeys.join(',') : form.columnKeys;
        form.departmentIds = (reportRequest.departmentIds) ? reportRequest.departmentIds.join(',') : form.departmentIds;
        form.disciplineIds = (reportRequest.disciplineIds) ? reportRequest.disciplineIds.join(',') : form.disciplineIds;
        form.generation = (reportRequest.generation) ? reportRequest.generation : form.generation;
        form.milestoneIds = getMilestoneIds(reportRequest.isMilestone);
        form.locationTypeIds = (reportRequest.locationTypeIds) ? reportRequest.locationTypeIds.join(',') : form.locationTypeIds;
        form.managementCompanyIds = (reportRequest.managementCompanyIds) ? reportRequest.managementCompanyIds.join(',') : form.managementCompanyIds;
        form.projectId = (reportRequest.projectId) ? reportRequest.projectId : form.projectId;
        form.projectZeroDate = getDateValue(reportRequest.projectZeroDate);
        form.regionIds = (reportRequest.regionIds) ? reportRequest.regionIds.join(',') : form.regionIds;

        this.setState({
            ...this.state,
            ...form
        });

        /** 
         * Make sure to trigger the parent's loading of the proper project dependent
         * filter values
         */
        if (reportRequest.projectId) {
            this.props.onProjectChange(reportRequest.projectId);
        }
    }

    /**
     * When an API request is going to be made, we also want to save the state
     * of our form entries into redux, so that saved state can be loaded when
     * this report is scheduled or simply reloaded later.  To accomplish that
     * from the centralized ReportDataHelper, we return both the apiRequest
     * object as well as the current state of our form.  This separation allows
     * for differences in the apiRequest format/naming conventions and those of
     * the form fields.
     */
    mapToApiRequest = () => {
        let isMilestone = null; // default to none or both
        if (this.state.milestoneIds.length > 0) {
            const selectedMilestoneIds = this.state.milestoneIds.split(',');
            if (selectedMilestoneIds.length == 1) {
                isMilestone = selectedMilestoneIds[0] === '1' ? true : false;
            }
        }

        let columnKeys = null;
        if (this.state.excludedColumnIds && this.state.excludedColumnIds.trim().length > 0) {
            columnKeys = this.state.excludedColumnIds.split(',');
        }

        const request = {
            reportId: this.props.report.id,

            areaIds: CsvToIntArray(this.state.areaIds),
            baselineIds: CsvToIntArray(this.state.baselineIds),
            brandIds: CsvToIntArray(this.state.brandIds),
            continentIds: CsvToIntArray(this.state.continentIds),
            countryIds: CsvToIntArray(this.state.countryIds),
            columnKeys: columnKeys,
            departmentIds: CsvToIntArray(this.state.departmentIds),
            disciplineIds: CsvToIntArray(this.state.disciplineIds),
            generation: this.state.generation,
            isMilestone: isMilestone,
            locationTypeIds: CsvToIntArray(this.state.locationTypeIds),
            managementCompanyIds: CsvToIntArray(this.state.managementCompanyIds),
            projectId: this.state.projectId,
            projectZeroDate: this.state.projectZeroDate,
            regionIds: CsvToIntArray(this.state.regionIds)
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({
            ...this.state,
            [name]: value
        });
    }

    onTextInputChange = (e) => {
        this.onFieldChange(e.target.name, e.target.value);
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    onDatePickerChange = (date, name) => {
        this.onFieldChange(name, date);
    }

    onToggleShowMore = () => {
        this.setState({
            showMoreFields: !this.state.showMoreFields
        });
    }

    onProjectChange = (value, name) => {
        // Save the changed value in our local state, as usual
        this.onFieldChange(name, value);

        // Tell our parent the project value has changed so they can reload the
        // options for the Project Zero Date and Baseline fields
        if (value) {
            this.props.onProjectChange(value);
        }
    }

    render() {
        const showMoreText = this.state.showMoreFields ? "Show Less Filters" : "Show More Filters";

        /**
         * Project Dependent Filters don't exist until a project has been selected,
         * so we must ensure we send MultiSelectBox an empty array instead of undefined
         * or it throws errors
         */
        const projectZeroDateOptions = this.props.filters.projectZeroDates ? this.props.filters.projectZeroDates : [];
        const projectBaselineOptions = this.props.filters.projectBaselines ? this.props.filters.projectBaselines : [];

        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectId"
                            label="Project" idField="id" valueField="name"
                            options={this.props.filters.projects}
                            selectedValues={this.state.projectId.toString()}
                            onChange={this.onProjectChange}
                            isRequired={true}
                            errorMessage="Project is required"
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="projectZeroDate"
                            label="Project Zero Date / Anticipated Completion" idField="id" valueField="name"
                            options={projectZeroDateOptions}
                            selectedValues={this.state.projectZeroDate.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="baselineIds"
                            label="Baseline" idField="id" valueField="name"
                            options={projectBaselineOptions}
                            selectedValues={this.state.baselineIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="brandIds"
                            label="Brand" idField="id" valueField="name"
                            options={this.props.filters.brands}
                            selectedValues={this.state.brandIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="locationTypeIds"
                            label="Location Type" idField="id" valueField="name"
                            options={this.props.filters.locationTypes}
                            selectedValues={this.state.locationTypeIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="continentIds"
                            label="Continent" idField="id" valueField="name"
                            options={this.props.filters.continents}
                            selectedValues={this.state.continentIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="areaIds"
                            label="Area" idField="id" valueField="name"
                            options={this.props.filters.areas}
                            selectedValues={this.state.areaIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="milestoneIds"
                            label="Milestone" idField="id" valueField="name"
                            options={this.props.filters.milestones}
                            selectedValues={this.state.milestoneIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="excludedColumnIds"
                            label="Exclude Columns" idField="id" valueField="name"
                            options={this.state.excludedColumnOptions}
                            selectedValues={this.state.excludedColumnIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                {
                    this.state.showMoreFields &&
                    <Fragment>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <InputText name="generation" type="text"
                                    label="Generation"
                                    placeholder="Generation"
                                    value={this.state.generation}
                                    onChange={this.onTextInputChange}
                                    isRequired={false} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="managementCompanyIds"
                                    label="Management Company" idField="id" valueField="name"
                                    options={this.props.filters.managementCompanies}
                                    selectedValues={this.state.managementCompanyIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="regionIds"
                                    label="Region" idField="id" valueField="name"
                                    options={this.props.filters.regions}
                                    selectedValues={this.state.regionIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="departmentIds"
                                    label="Department" idField="id" valueField="name"
                                    options={this.props.filters.departments}
                                    selectedValues={this.state.departmentIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="countryIds"
                                    label="Country" idField="id" valueField="name"
                                    options={this.props.filters.countries}
                                    selectedValues={this.state.countryIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                            <div className="col-sm-12 col-md-4">
                                <MultiSelectBox name="disciplineIds"
                                    label="Discipline" idField="id" valueField="name"
                                    options={this.props.filters.disciplines}
                                    selectedValues={this.state.disciplineIds.toString()}
                                    onChange={this.onDropdownChange}
                                    isRequired={false}
                                    isMulti={true} />
                            </div>
                        </div>
                    </Fragment>
                }
                <div className="row text-end">
                    <div className="col-sm-12">
                        <Button name="showMore" type="button" bootstrapClass="link" text={showMoreText} onClick={this.onToggleShowMore} />
                    </div>
                </div>
            </form>
        );
    }
}

ProjectCrosstabFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,

    registerReportFilterFunctions: PropTypes.func.isRequired,
    onProjectChange: PropTypes.func.isRequired
};

export default ProjectCrosstabFilters;
